<template>
  <div>
    <Breadcrumbs />
    <ErrorMessage v-if="errorMessage">
      <p>{{ errorMessage }}</p>
      <p>
        <ThreeDButton @click="fetchBookData">
          {{ $t('actions.retry') }}
        </ThreeDButton>
      </p>
    </ErrorMessage>
    <LoadingOverlay v-else-if="!bookData" />
    <article v-else class="book">
      <div class="book-wrapper">
        <header
          ref="titleWrapper"
          class="book-title-wrapper"
          :class="{ 'touches-navigation': titleTouchesMainNavigation }"
        >
          <h3 class="book-title">{{ bookName }}</h3>
        </header>
        <div class="chapter-wrapper">
          <nav class="chapter-navigation">
            <RouterLink
              ref="indexPageLink"
              :to="{ name: 'book', params: { slug: bookSlug } }"
              class="chapter-navigation-link table-of-contents"
            >
              <FaIcon icon="list" />
            </RouterLink>
            <ol
              ref="chapterNavigationList"
              class="chapter-navigation-list"
              :class="{ sticky: stickyChapterNavigation }"
            >
              <li
                class="chapter-navigation-list-item"
                v-for="(chapter, index) in chapters"
                :key="`chapter-navigation-link-${index}`"
              >
                <RouterLink
                  class="chapter-navigation-link"
                  :title="
                    $t('book-shelf.chapter') +
                      ' ' +
                      (index + 1) +
                      ': ' +
                      chapter.name
                  "
                  :to="{
                    name: 'book-chapter',
                    params: {
                      chapterSlug: createSlug(chapter.name, chapter.id),
                    },
                  }"
                  >{{ index + 1 }}</RouterLink
                >
              </li>
            </ol>
          </nav>
          <RouterView />
        </div>
      </div>
    </article>
  </div>
</template>

<script>
import Breadcrumbs from '../navigation/Breadcrumbs'
import ErrorMessage from '@/components/ui/ErrorMessage'
import FaIcon from '@/components/ui/FaIcon'
import LoadingOverlay from '@/components/LoadingOverlay'
import ThreeDButton from '@/components/ThreeDButton'
import { AuthStoreNamespacedTypes } from '@/store/auth'
import { BookStoreNamespacedTypes } from '@/store/books'
import { getProperty } from '@/utils/object'
import { createSlug, getIdFromSlug } from '@/utils/string'
import { cssSizeToPx } from '@/utils/style'
import { UserType } from '@/enums'

export default {
  name: 'Book',
  components: {
    Breadcrumbs,
    ErrorMessage,
    FaIcon,
    LoadingOverlay,
    ThreeDButton,
  },
  props: {
    sectionSlug: {
      type: String,
      required: false,
    },
    chapterSlug: {
      type: String,
      required: false,
    },
    bookSlug: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      UserType,
      /**
       * Whether the book title touches the main navigation.
       * @type {boolean}
       */
      titleTouchesMainNavigation: false,

      /**
       * The height of the main navigation.
       * @type {number}
       */
      mainNavigationHeight: 0,

      /**
       * Whether the chapter navigation should be sticky.
       * @type {boolean}
       */
      stickyChapterNavigation: false,
      reRenderChapterView: this.chapterSlug,
    }
  },
  computed: {
    /**
     * The ID of the book
     * @returns {number}
     */
    id() {
      return getIdFromSlug(this.bookSlug)
    },

    /**
     * The name of the book
     * @returns {string}
     */
    bookName() {
      return getProperty(this.bookData, 'name')
    },

    /**
     * The complete data for the book
     * @returns {Object}
     */
    bookData() {
      return this.$store.getters[BookStoreNamespacedTypes.getters.GET_BOOK](
        this.id
      )
    },

    /**
     * Any book loading error messages.
     * @returns {String}
     */
    errorMessage() {
      return this.$store.getters[
        BookStoreNamespacedTypes.getters.GET_BOOK_ERROR
      ](this.id)
    },

    /**
     * The chapters for the book
     * @returns {Array<Object>}
     */
    chapters() {
      return this.$store.getters[BookStoreNamespacedTypes.getters.GET_CHAPTERS](
        this.id
      )
    },

    /**
     * The type of the current user
     * @returns {UserType}
     */
    userType() {
      return this.$store.getters[
        AuthStoreNamespacedTypes.getters.GET_USER_TYPE
      ]()
    },
  },
  methods: {
    createSlug,

    fetchBookData() {
      this.$store
        .dispatch(BookStoreNamespacedTypes.actions.FETCH_BOOK, this.id)
        .then(() => {
          // Update style
          this.onWindowResize()
        })
    },

    /**
     * Callback for resize events on the window.
     */
    onWindowResize() {
      // Get the main navigation height compensation from a CSS custom property
      // The value of the property is controlled by a media query, so we can't just measure the element
      this.mainNavigationHeight = cssSizeToPx(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--menu-height-compensation'
        )
      )

      this.updateBookTitleStyle()
      this.updateChapterNavigationStickiness()
    },

    /**
     * Callback for scroll events on the window.
     */
    onWindowScroll() {
      this.updateBookTitleStyle()
    },

    /**
     * Updates the style for .book-title-wrapper.
     * Will keep retrying if the element hasn't been rendered when called.
     */
    updateBookTitleStyle() {
      if (!this.$refs.titleWrapper) {
        if (!this._isDestroyed) {
          // The elements hasn't yet been rendered. Trying again in 50 ms.
          setTimeout(this.updateBookTitleStyle, 50)
        }

        return
      }

      // Change the style of the book title if it touches the main navigation
      this.titleTouchesMainNavigation =
        this.$refs.titleWrapper.getBoundingClientRect().top <=
        this.mainNavigationHeight
    },

    /**
     * Updates the stickiness of the chapter navigation.
     * Will keep retrying if the element hasn't been rendered when called.
     */
    updateChapterNavigationStickiness() {
      if (!this.$refs.indexPageLink) {
        if (!this._isDestroyed) {
          // The elements hasn't yet been rendered. Trying again in 50 ms.
          setTimeout(this.updateChapterNavigationStickiness, 50)
        }

        return
      }

      // Get the static height of the index page link
      const indexPageLinkHeight = this.$refs.indexPageLink.$el.offsetHeight

      // Make the navigation stick if there is enough space on the screen to display all of the chapters at once
      this.stickyChapterNavigation =
        window.innerHeight - indexPageLinkHeight - this.mainNavigationHeight >
        this.$refs.chapterNavigationList.scrollHeight
    },
  },
  mounted() {
    this.onWindowResize()

    window.addEventListener('resize', this.onWindowResize)
    window.addEventListener('scroll', this.onWindowScroll)

    this.fetchBookData()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize)
    window.removeEventListener('scroll', this.onWindowScroll)
  },
}
</script>

<style lang="scss" scoped>
.book {
  --spacer-1: 1em;
  --border-radius: 0.95em;
  --book-border-radius: var(--border-radius);

  @media (max-width: $maxWidthDesktop) {
    --book-border-radius: 0;
  }
}

.loading-overlay {
  background: none;
}

.book-wrapper {
  border-radius: var(--book-border-radius);
}

.chapter-wrapper {
  display: grid;
  grid-template-columns: $chapterNavigationWidth calc(
      100% - #{$chapterNavigationWidth}
    );
  background: $grayLighter;
  border-radius: 0 0 var(--book-border-radius) var(--book-border-radius);
}

.book-title-wrapper {
  position: sticky;
  top: var(--menu-height-compensation);
  padding: calc(var(--spacer-1) * 1.25) 0;
  background: $orange;
  z-index: 4;

  &:not(.touches-navigation) {
    border-radius: var(--book-border-radius) var(--book-border-radius) 0 0;
  }
}

.book-title {
  font-size: calc(var(--spacer-1) * 1.5);
  font-weight: 600;
  color: $white;
  text-transform: uppercase;
  margin: 0;
  text-align: center;
}

.chapter-navigation {
  display: flex;
  flex-direction: column;
  background: $blueSemiDark;
  border-bottom-left-radius: var(--book-border-radius);
}

.chapter-navigation-list {
  // Reset global Fessor style
  margin: 0;

  &.sticky {
    position: sticky;

    // Compensate for the main navigation menu, .book-title-wrapper and .index-page-link
    top: calc(var(--menu-height-compensation) + 3.1875rem + 3rem);
  }
}

.chapter-navigation-list-item {
  list-style: none;

  // Reset global Fessor style
  margin: 0;
}

.chapter-navigation-link {
  display: flex;
  text-align: center;
  flex-direction: column;
  justify-content: center;
  text-decoration: none;
  color: $white;
  padding: calc(var(--spacer-1) * 1.5) calc(var(--spacer-1) * 1.75);

  &:hover,
  &.router-link-active:not(.table-of-contents),
  &.router-link-exact-active.table-of-contents {
    background: $blueDark;
    text-decoration: none;
  }

  &.table-of-contents {
    position: sticky;
    box-sizing: border-box;
    width: $chapterNavigationWidth;
    background: $blueSemiDark;
    z-index: 1;

    // Compensate for the main navigation menu and .book-title-wrapper
    top: calc(var(--menu-height-compensation) + 3.1875rem);
  }
}

.chapter-content {
  padding: calc(var(--spacer-1) * 3);
  flex-basis: 0;
  flex-grow: 999;
}

@media (max-width: $minWidthSmallTablet) {
  .chapter-wrapper {
    grid-template-columns: 0 100%;
  }
  .chapter-navigation {
    background: transparent;
  }
  .chapter-navigation-list {
    display: none;
  }
  .chapter-navigation-link.table-of-contents {
    position: static;

    &.router-link-exact-active {
      display: none;
    }
  }
}
</style>
